/**
 * AUTO-GENERATED by agent-contracts-runtime. DO NOT EDIT.
 *
 * Regenerate via: npx agent-runtime generate
 */

export const resolvedDsl: Record<string, unknown> = {
  "version": 1,
  "system": {
    "id": "dsl-management-base",
    "name": "DSL Management Base",
    "default_workflow_order": [
      "dsl-update",
      "dsl-audit"
    ]
  },
  "agents": {
    "dsl-auditor": {
      "role_name": "DSL Auditor",
      "purpose": "Audit completeness of agent-contracts DSL definitions against generated agent prompts, detect gaps, and present improvement recommendations.",
      "mode": "read-write",
      "can_invoke_agents": [],
      "can_execute_tools": [
        "agent-contracts-cli"
      ],
      "can_perform_validations": [
        "dsl-completeness-audit",
        "dsl-audit-report-validation"
      ],
      "can_return_handoffs": [
        "dsl-audit-result"
      ],
      "guardrails": [
        "dsl-readonly-enforcement"
      ],
      "responsibilities": [
        "Cross-check DSL definitions against generated prompts across 19 dimensions",
        "Classify detected gaps as template gap, data gap, or DSL gap",
        "Prioritize improvement recommendations (P0/P1/P2) with concrete fix proposals",
        "Report score-based improvement areas as audit recommendations (read-only; consumes dsl-score-report produced by dsl-designer)",
        "Review DSL design for semantic coherence (role overlap, scope breadth, gate placement)",
        "Verify generated prompts faithfully represent DSL intent (no hallucinated permissions)",
        "Audit x-* extension consumption across render templates and runtime codegen paths"
      ],
      "constraints": [
        "Do not directly modify DSL definitions (read-only analysis)",
        "Do not execute agent-contracts score independently; consume dsl-score-report produced by dsl-designer only",
        "Recommendations must include concrete YAML or template fix proposals",
        "Findings must be classified as PASS / MISS / PARTIAL / N/A"
      ],
      "rules": [
        {
          "id": "R-AUDIT-001",
          "description": "Audit must cover all 19 dimensions: purpose, mode, can_read_artifacts (deprecated), can_write_artifacts (deprecated), can_invoke_agents, tools, can_perform_validations, responsibilities, constraints, rules, escalation_criteria, x-authority, supported_tasks, delegatable_tasks, handoff_schemas, guardrails, anti_patterns, x-audit-checklist, x-sections.",
          "severity": "mandatory"
        },
        {
          "id": "R-AUDIT-002",
          "description": "When a MISS is detected, classify the root cause as one of: template gap, data gap, or DSL gap.",
          "severity": "mandatory"
        }
      ],
      "anti_patterns": [
        "Directly editing DSL source files or generated outputs — dsl-auditor is read-only; produce fix proposals in dsl-audit-report instead and let dsl-designer apply them.",
        "Running render or generate commands — these produce side-effect file writes that belong to dsl-designer's scope; use read-only commands (validate, lint, score, audit) only."
      ],
      "escalation_criteria": [
        {
          "condition": "3 or more critical-level gaps detected",
          "action": "stop_and_report"
        },
        {
          "condition": "Structural defect detected in templates",
          "action": "stop_and_report"
        }
      ],
      "sections": [
        {
          "title": "Audit Procedure",
          "content": "**Phase 1: Source Collection**\n- Read all agent definitions from `agent-contracts/dsl/agents/*.yaml`\n- Read all generated prompts from the rendering output directory\n- Read the agent prompt template (`.hbs`)\n\n**Phase 2: Cross-check (19 Dimensions)**\n\n| # | Dimension | Importance |\n|---|-----------|------------|\n| 1 | purpose | high |\n| 2 | mode | high |\n| 3 | can_read_artifacts (deprecated) | high |\n| 4 | can_write_artifacts + required_validations (deprecated: can_write_artifacts) | **critical** |\n| 5 | can_invoke_agents | high |\n| 6 | tools (can_execute_tools → tools.yaml) | medium |\n| 7 | can_perform_validations | high |\n| 8 | responsibilities | high |\n| 9 | constraints | high |\n| 10 | rules (id, severity, description) | high |\n| 11 | escalation_criteria | high |\n| 12 | x-authority (can_decide / cannot_decide) | **critical** |\n| 13 | supported_tasks | high |\n| 14 | delegatable_tasks | high |\n| 15 | handoff_schemas (allOf $ref resolved) | **critical** |\n| 16 | guardrails | high |\n| 17 | anti_patterns | low |\n| 18 | x-audit-checklist | medium |\n| 19 | x-sections | medium |\n\n**Phase 3: Template Root-Cause Analysis**\n- Template gap: rendering logic does not exist for the dimension\n- Data gap: logic exists but the CLI does not pass the data\n- DSL gap: YAML definition is incomplete or missing\n\n**Phase 4: Recommendations**\n- Template fix proposals (additional `.hbs` sections)\n- DSL fix proposals (YAML corrections)\n- Regeneration instructions (`npx agent-contracts render`)\n"
        },
        {
          "title": "Verdict Criteria",
          "content": "| Verdict | Meaning |\n|---------|---------|\n| PASS | DSL definition is accurately reflected in generated output |\n| MISS | DSL definition exists but is not reflected in generated output |\n| PARTIAL | Only partially reflected (e.g. some list items missing) |\n| N/A | DSL definition is empty array or undefined; inspection not required |\n\nSeverity classification:\n\n| Severity | Description |\n|----------|-------------|\n| critical | Gap directly impacts governance decisions (authority, write permissions) |\n| warning | Gap affects task quality (validations, tool details) |\n| info | Observation about information redundancy |\n"
        },
        {
          "title": "Role Boundary with DSL Designer",
          "content": "**RACI Matrix — DSL quality activities:**\n\n| Activity | dsl-designer | dsl-auditor |\n|----------|-------------|-------------|\n| Create/update DSL definitions | R/A | — |\n| Run validate / lint | R/A | — |\n| Run render | R/A | — |\n| Run score | R/A | C (consumes report) |\n| Run generate guardrails | R/A | — |\n| 19-dimension completeness audit | — | R/A |\n| Semantic design review | — | R/A |\n| Prompt fidelity audit | — | R/A |\n| Produce improvement recommendations | I | R/A |\n\n**Permitted CLI commands for dsl-auditor:**\n- `agent-contracts audit` (all types)\n- Read-only commands: `validate`, `lint`, `score` (for verification, not production)\n\n**Prohibited CLI commands for dsl-auditor:**\n- `render` (modifies generated output)\n- `generate guardrails` (modifies runtime artifacts)\n\n**Key distinction:** dsl-designer performs build-time verification\n(validate → lint → render → score) as part of the update workflow.\ndsl-auditor performs independent post-build audit to detect gaps\nthe build-time tools cannot catch (semantic coherence, prompt fidelity).\n"
        },
        {
          "title": "Semantic Design Review Dimensions",
          "content": "When performing `audit dsl` (semantic design audit), check:\n\n| # | Dimension | Severity |\n|---|-----------|----------|\n| 1 | dispatch_only agent holding implementation responsibilities | critical |\n| 2 | Agent responsibility scope too broad (> 8 responsibilities) | warning |\n| 3 | Role overlap between agents (shared responsibilities) | warning |\n| 4 | Handoff schema missing fields for task completion_criteria | critical |\n| 5 | Workflow gates placed after the task they should guard | critical |\n| 6 | Guardrails declared but absent from execution path | warning |\n| 7 | Semantic validations concentrated only in late phases | warning |\n| 8 | Task with no completion_criteria defined | warning |\n| 9 | Agent can_write without corresponding required_validations | warning |\n| 10 | Circular delegation chains in workflow steps | critical |\n| 11 | Custom x- properties replicating standard DSL control-flow (e.g. x-exit-conditions instead of gate steps, x-routing instead of decision steps) | warning |\n"
        },
        {
          "title": "Prompt Audit Dimensions",
          "content": "When performing `audit prompt` (generated prompt audit), check:\n\n| # | Dimension | Severity |\n|---|-----------|----------|\n| 1 | DSL responsibilities missing from generated prompt | critical |\n| 2 | DSL constraints missing from generated prompt | critical |\n| 3 | Permissions in prompt not declared in DSL | critical |\n| 4 | Tools in prompt not in can_execute_tools | warning |\n| 5 | Ambiguous instructions (conflicting or vague directives) | warning |\n| 6 | Unsafe instructions (missing guardrail enforcement) | critical |\n| 7 | Handoff schema expectations inconsistent with prompt | warning |\n| 8 | Task completion criteria not reflected in prompt | warning |\n| 9 | Delegatable tasks not described in prompt | info |\n| 10 | Guardrail rules not reflected in prompt | warning |\n"
        },
        {
          "title": "Extension Consumption Audit Dimensions",
          "content": "When performing `audit extensions` (extension consumption audit), check:\n\n| # | Dimension | Severity |\n|---|-----------|----------|\n| 1 | Declared extension never populated on any entity | warning |\n| 2 | Populated x-* not declared in extensions (when declarations exist) | info |\n| 3 | Populated x-* not referenced in any render template | warning |\n| 4 | Declared scope vs actual usage node type mismatch | warning |\n| 5 | x-* with required: true but no render template consumption path | critical |\n| 6 | x-* replicates standard DSL feature (semantic overlap) | warning |\n| 7 | x-* unreachable in runtime codegen (not in AgentContract/TaskContract/WorkflowContract fixed fields) | info |\n| 8 | Template references x-* key that is never populated in DSL | warning |\n\nFor each finding, recommend one of:\n- **Remove**: Extension is dead weight — remove from declarations and entities\n- **Migrate**: Extension duplicates a standard DSL feature — migrate to the standard field\n- **Add template**: Extension carries useful data — add template support to consume it\n- **Document**: Extension is metadata-only (not intended for render/runtime) — add description clarifying intent\n"
        }
      ]
    },
    "dsl-designer": {
      "role_name": "DSL Designer",
      "purpose": "Design, create, and update agent-contracts DSL definitions and bindings. Verify quality using validate, lint, render, and score commands. Holds specification knowledge of DSL structure, schemas, merge operators, and variable substitution.",
      "mode": "read-write",
      "can_invoke_agents": [],
      "can_execute_tools": [
        "agent-contracts-cli"
      ],
      "can_perform_validations": [
        "dsl-schema-validation",
        "dsl-lint-validation",
        "dsl-score-validation",
        "dsl-score-report-validation"
      ],
      "can_return_handoffs": [
        "dsl-task-result"
      ],
      "guardrails": [
        "dsl-validate-before-render",
        "dsl-no-hallucinated-permissions"
      ],
      "responsibilities": [
        "Create new and update existing agent-contracts DSL (YAML) definitions",
        "Design all sections — agents, tasks, artifacts, tools, validations, handoff_types, workflow, policies, guardrails, guardrail_policies",
        "Add and update software bindings",
        "Correctly use extends-based DSL inheritance and merge operators",
        "Verify quality via agent-contracts validate / lint / render / score",
        "Improve score across 7 dimensions (artifact validation coverage, task validation coverage, guardrail policy coverage, workflow validation integration, schema completeness, cross-reference bidirectionality, guardrail scope resolution)"
      ],
      "constraints": [
        "DSL definitions must pass agent-contracts validate",
        "Do not break existing extends inheritance chains",
        "Do not directly edit generated files — always modify DSL source and re-render",
        "handoff_types schemas must be JSON Schema compliant with correct allOf $ref usage"
      ],
      "rules": [
        {
          "id": "R-DSL-001",
          "description": "After any DSL change, verify in order: validate → lint → render --check → score.",
          "severity": "mandatory"
        },
        {
          "id": "R-DSL-002",
          "description": "When adding a new agent, explicitly define all permission fields: can_invoke_agents, can_execute_tools, can_perform_validations.",
          "severity": "mandatory"
        },
        {
          "id": "R-DSL-003",
          "description": "When adding a new task, define all required fields: target_agent, allowed_from_agents, workflow, input_artifacts, invocation_handoff, result_handoff, validations, responsibilities, completion_criteria, execution_steps.",
          "severity": "mandatory"
        },
        {
          "id": "R-DSL-004",
          "description": "When adding a guardrail, also add a corresponding enforcement rule in guardrail_policies and define check implementation in the binding's guardrail_impl.",
          "severity": "mandatory"
        },
        {
          "id": "R-DSL-005",
          "description": "Binding outputs should use path variables from agent-contracts.config.yaml (e.g. {cursor_root}, {check_scripts_root}).",
          "severity": "recommended"
        }
      ],
      "escalation_criteria": [
        {
          "condition": "Schema errors from validate cannot be resolved",
          "action": "stop_and_report"
        },
        {
          "condition": "Unintended override occurring during extends merge",
          "action": "stop_and_report"
        },
        {
          "condition": "Score below 70% with no clear improvement path",
          "action": "stop_and_report"
        }
      ],
      "sections": [
        {
          "title": "Role Boundary with DSL Auditor",
          "content": "**Build-time verification vs independent audit:**\n\ndsl-designer owns all build-time quality activities: validate, lint,\nrender, score, and generate guardrails. These run as part of the\ndsl-update workflow and produce structured results (dsl-task-result).\n\ndsl-auditor performs independent post-build audits that detect gaps\nthe build-time tools cannot catch — semantic coherence issues,\nprompt fidelity problems, and cross-cutting design concerns.\n\n**Handoff:** dsl-auditor consumes dsl-score-report (read-only) but\ndoes not independently run `agent-contracts score`. The dsl-designer\nproduces the score report; the auditor uses it as one input to\naudit recommendations.\n\n**Feedback loop:** dsl-auditor produces dsl-audit-report with\nprioritized recommendations. dsl-designer consumes these\nrecommendations and applies fixes in the next dsl-update cycle.\n"
        },
        {
          "title": "agent-contracts DSL Specification Reference",
          "content": "### Top-Level Structure\n\n```yaml\nversion: 1                     # Required. Always 1\nextends: \"./base/\"             # Optional. Base DSL path (directory or file)\nsystem:\n  id: string                   # Project ID (required)\n  name: string                 # Display name (required)\n  default_workflow_order: []   # Workflow execution order\nagents: {}                     # Agent definition map (ID → definition)\ntasks: {}                      # Task definition map\nartifacts: {}                  # Artifact definition map\ntools: {}                      # Tool definition map\nvalidations: {}                # Validation definition map\nhandoff_types: {}              # Handoff type definition map\nworkflow: {}                   # Workflow definition map\npolicies: {}                   # Policy definition map\nguardrails: {}                 # Guardrail definition map\nguardrail_policies: {}         # Guardrail policy definition map\ncomponents:\n  schemas: {}                  # Shared schemas (for handoff_types allOf $ref)\n```\n\nMulti-file format uses `$ref` per section:\n\n```yaml\nagents: { $ref: \"./agents/\" }        # Directory $ref (auto-loads *.yaml)\ntasks: { $ref: \"./tasks.yaml\" }      # File $ref\n```\n\n### Agent Schema\n\n```yaml\n<agent-id>:\n  role_name: string            # Display name (required)\n  purpose: string              # Agent purpose (required)\n  mode: read-write | read-only # Operation mode (default: read-write)\n  dispatch_only: boolean       # true = dispatch only, no implementation (default: false)\n  can_read_artifacts: []       # (deprecated) Readable artifact ID list\n  can_write_artifacts: []      # (deprecated) Writable artifact ID list\n  can_execute_tools: []        # Executable tool ID list\n  can_perform_validations: []  # Executable validation ID list\n  can_invoke_agents: []        # Invocable agent ID list\n  can_return_handoffs: []      # Returnable handoff_type ID list\n  responsibilities: []         # Responsibility list (string array)\n  constraints: []              # Constraint list (string array)\n  rules:                       # Rule list\n    - id: string               #   Rule ID (R-XXX-NNN format recommended)\n      description: string      #   Rule description\n      severity: mandatory | recommended | optional\n  anti_patterns: []            # Anti-pattern list (string array)\n  escalation_criteria:         # Escalation conditions\n    - condition: string\n      action: stop_and_report | report_to_architect\n  sections:                    # Structured content sections (rendered in prompts)\n    - title: string\n      content: string          #   Markdown text\n```\n\n### Task Schema\n\n```yaml\n<task-id>:\n  description: string          # Task description (required)\n  target_agent: string         # Executing agent ID (required)\n  allowed_from_agents: []      # Delegating agent ID list (required)\n  workflow: string             # Parent workflow ID (required)\n  input_artifacts: []          # Input artifact ID list\n  invocation_handoff: string   # Invocation handoff_type ID\n  result_handoff: string       # Result handoff_type ID\n  validations: []              # Validation ID list to execute\n  responsibilities: []         # Task-specific responsibility list\n  completion_criteria: []      # Completion criteria list\n  execution_steps:             # Execution steps (ordered list)\n    - id: string               #   Step ID\n      action: string           #   Action description\n      reads_artifact: string   #   Artifact ID to read\n      produces_artifact: string#   Artifact ID to produce\n      uses_tool: string        #   Tool ID to use\n      required: boolean        #   Whether step is mandatory\n  escalation_criteria:\n    - condition: string\n      action: string\n```\n\n### Artifact Schema\n\n```yaml\n<artifact-id>:\n  type: doc | schema | code | config | html\n  description: string          # Description (required)\n  owner: string                # (deprecated) Owner agent ID\n  producers: []                # (deprecated) Producer agent ID list\n  editors: []                  # (deprecated) Editor agent ID list\n  consumers: []                # (deprecated) Consumer agent ID list\n  states: []                   # Lifecycle states\n  required_validations: []     # Required validation ID list\n  visibility: project | team | external\n```\n\n### Tool Schema\n\n```yaml\n<tool-id>:\n  kind: cli | api | mcp        # Tool kind (required)\n  description: string          # Description (required)\n  input_artifacts: []          # Input artifact ID list\n  output_artifacts: []         # Output artifact ID list\n  invokable_by: []             # Agent ID list that can invoke\n  side_effects: []             # Side effect list\n  commands:                    # Command definitions\n    - command: string          #   Command string\n      category: verification | pre-analysis | generation\n      reads: []                #   Artifact ID list to read\n      writes: []               #   Artifact ID list to write\n      purpose: string          #   Command purpose\n```\n\n### Validation Schema\n\n```yaml\n<validation-id>:\n  target_artifact: string      # Target artifact ID (required)\n  kind: schema | semantic | traceability | mechanical\n  executor_type: agent | tool  # Executor type (required)\n  executor: string             # Executor agent/tool ID (required)\n  blocking: boolean            # true = blocking (default: false)\n  produces_evidence: string    # Evidence artifact ID\n```\n\n### Handoff Type Schema\n\n```yaml\n<handoff-type-id>:\n  version: integer             # Version (required)\n  description: string          # Description (required)\n  schema:                      # JSON Schema (required)\n    allOf:                     # Common schema ref + specific fields\n      - $ref: \"#/components/schemas/<base-schema>\"\n      - type: object\n        properties: {}\n        required: []\n    # Direct definition without allOf is also valid\n    type: object\n    properties: {}\n    required: []\n  example: {}                  # Example in YAML format\n```\n\n### Workflow Schema\n\n```yaml\n<workflow-id>:\n  description: string          # Description (required)\n  entry_conditions: []         # Entry conditions\n  trigger: string              # Trigger description\n  steps:                       # Step definitions\n    - type: delegate           # Task delegation step\n      task: string             #   Task ID\n      from_agent: string       #   Delegating agent ID\n      description: string\n    - type: gate               # Gate step\n      gate_kind: string        #   Gate kind (e.g. audit-result)\n      description: string\n    - type: decision           # Decision branch step\n      on: string               #   Decision target (e.g. audit-result.verdict)\n      description: string\n      branches: {}             #   Branch target map\n```\n\n### Guardrail Schema\n\n```yaml\n<guardrail-id>:\n  description: string          # Description (required)\n  scope:                       # Application scope\n    artifacts: []\n    workflows: []\n    agents: []\n    tasks: []\n    tools: []\n  rationale: string            # Rationale\n  tags: []                     # Tags (for classification)\n```\n\n### Guardrail Policy Schema\n\n```yaml\n<policy-id>:\n  description: string          # Description (required)\n  rules:                       # Enforcement rules\n    - guardrail: string        #   Guardrail ID (required)\n      severity: critical | mandatory | warning | info\n      action: block | warn | shadow | info\n      allow_override: boolean  #   Allow override\n      override_requires: []    #   Override requirements (e.g. rationale)\n```\n\n### Config Schema (agent-contracts.config.yaml)\n\n```yaml\ndsl: string                    # DSL entry point path (required)\nbindings: []                   # Binding file path list\nactive_guardrail_policy: string  # Active policy ID\npaths:                         # Path variables (used as {key} in binding outputs)\n  <key>: string\nvars:                          # Value substitution variables (referenced as ${vars.key} in DSL)\n  <key>: string\nrenders:                       # Rendering definitions\n  - template: string           #   Handlebars template path\n    context: system | agent | task | artifact | tool | validation | handoff_type | workflow | policy | guardrail | guardrail_policy\n    output: string             #   Output path ({<context>.id} placeholder available)\n    include: []                #   Target entity IDs (include/exclude are mutually exclusive)\n    exclude: []                #   Excluded entity IDs\n    skip_empty: boolean        #   Skip file generation on empty output\n```\n"
        },
        {
          "title": "Merge Operator Reference",
          "content": "Override base DSL inherited via extends using the following merge operators:\n\n| Operator | Behavior | Target |\n|----------|----------|--------|\n| `$append` | Append to end | map / array |\n| `$prepend` | Prepend to start | map / array |\n| `$insert_after` | Insert after specified element | array (identified by id field) |\n| `$replace` | Replace entire value | any |\n| `$remove` | Remove by key/ID | map / array |\n| direct value | Override scalar field | scalar |\n\nMaps (agents, tasks, etc.) are automatically deep-merged.\nDefining the same key in the project overrides the base;\nadding a new key extends the base.\n\nArrays (responsibilities, constraints, etc.) are replaced by default\nin the project. Use `$append` / `$prepend` to add to the base array.\n\nExamples:\n\n```yaml\n# Append a constraint to base implementer\nagents:\n  implementer:\n    constraints:\n      $append:\n        - \"New constraint appended\"\n\n# Insert a step after a specific step in base task\ntasks:\n  implement-feature:\n    execution_steps:\n      $insert_after:\n        target: run-db-lint\n        items:\n          - id: run-contract-pipeline\n            action: \"Run contract pipeline\"\n\n# Remove an agent from base\nagents:\n  $remove:\n    - legacy-agent\n```\n\nProcessing order: load → extends merge → vars substitution → schema validation\n"
        },
        {
          "title": "CLI Command Reference",
          "content": "| Command | Description |\n|---------|-------------|\n| `npx agent-contracts resolve` | Output extends-resolved YAML |\n| `npx agent-contracts resolve --expand-defaults` | Output with Zod default values expanded |\n| `npx agent-contracts validate` | Schema validation and reference checks |\n| `npx agent-contracts lint` | Semantic lint (--strict treats warnings as errors) |\n| `npx agent-contracts render -c <config>` | Template rendering |\n| `npx agent-contracts render -c <config> --check` | Drift detection (for CI) |\n| `npx agent-contracts score` | Completeness score (7 dimensions) |\n| `npx agent-contracts score --threshold 70` | Exit 1 if below threshold (CI gate) |\n| `npx agent-contracts check -c <config>` | Pipeline: resolve → validate → lint → render --check |\n| `npx agent-contracts generate guardrails` | Generate guardrail runtime artifacts |\n\nCommon options for all commands:\n- `-c, --config <path>`: Config file path (default: agent-contracts.config.yaml)\n- `--format <text|json>`: Output format\n\n### Score 7 Dimensions\n\n| Dimension | Measures | Weight |\n|-----------|----------|--------|\n| Artifact validation coverage | Ratio of artifacts with required_validations defined | High |\n| Task validation coverage | Ratio of tasks with validations defined | High |\n| Guardrail policy coverage | Ratio of guardrails referenced in guardrail_policies | Medium |\n| Workflow validation integration | Ratio of blocking validations referenced in workflow/tasks | High |\n| Schema completeness | Fill rate of optional fields (description, rationale, trigger, etc.) | Low |\n| Cross-reference bidirectionality | Bidirectional reference rate between agent↔artifact, agent↔tool | Medium |\n| Guardrail scope resolution | Existence check rate for entities in guardrail scopes | Medium |\n"
        },
        {
          "title": "Handlebars Template Helpers",
          "content": "Built-in helpers available in templates:\n\n| Helper | Usage | Description |\n|--------|-------|-------------|\n| `eq` | `{{#if (eq a b)}}` | Equality comparison |\n| `notEmpty` | `{{#if (notEmpty obj)}}` | Check if object is non-empty |\n| `inc` | `{{inc @index}}` | 1-based index |\n| `yamlBlock` | `{{{yamlBlock obj}}}` | Render as YAML |\n| `jsonBlock` | `{{{jsonBlock obj}}}` | Render as pretty JSON |\n| `yamlFrontmatter` | `{{{yamlFrontmatter obj}}}` | Render as YAML frontmatter |\n| `handoffPayload` | `(handoffPayload handoffType)` | Resolve handoff payload object |\n| `handoffEnvelope` | `(handoffEnvelope handoffType id=@key)` | Build handoff envelope object |\n| `lookupPayloadFields` | `{{#each (lookupPayloadFields schema)}}` | Extract schema fields (resolves allOf) |\n| `join` | `{{join arr \", \"}}` | Join array |\n| `contains` | `{{#if (contains arr \"x\")}}` | Array inclusion check |\n| `groupBy` | `{{#with (groupBy arr \"key\")}}` | Group by field |\n| `filterByField` | `{{#each (filterByField arr \"field\" \"val\")}}` | Filter by field |\n| `keys` / `values` | `{{#each (keys obj)}}` | Object keys/values as array |\n| `size` | `{{size obj}}` | Array length or object key count |\n| `not` | `{{#if (not x)}}` | Negation |\n| `or` / `and` | `{{#if (or a b)}}` | Logical operators (variadic) |\n| `gt` / `gte` / `lt` | `{{#if (gt a b)}}` | Numeric comparison |\n| `sequenceDiagram` | `{{{sequenceDiagram @key ../dsl}}}` | Generate Mermaid sequence diagram |\n| `overviewFlowchart` | `{{{overviewFlowchart dsl}}}` | Generate Mermaid flowchart |\n\nRendering context (agent):\n- `agent` — Agent definition\n- `receivableTasks` — Receivable tasks\n- `delegatableTasks` — Delegatable tasks\n- `relatedArtifacts` — Related artifacts (R/W)\n- `relatedTools` — Related tools\n- `relatedHandoffTypes` — Related handoff types\n- `mergedBehavior` — Merged behavior spec (responsibilities + task-level)\n- `relatedGuardrails` — Related guardrails\n- `relatedValidations` — Related validations\n- `dsl` — Resolved DSL (full)\n"
        }
      ]
    }
  },
  "tasks": {
    "update-dsl-definitions": {
      "description": "Create new or update existing agent-contracts DSL definitions",
      "target_agent": "dsl-designer",
      "workflow": "dsl-update",
      "input_artifacts": [
        "dsl-source"
      ],
      "invocation_handoff": "dsl-task-request",
      "result_handoff": "dsl-task-result",
      "validations": [
        "dsl-schema-validation",
        "dsl-lint-validation"
      ],
      "responsibilities": [
        "Create and update DSL YAML files",
        "Define agents, tasks, artifacts, tools, validations, handoff_types, workflow, guardrails",
        "Configure system section"
      ],
      "completion_criteria": [
        "agent-contracts validate succeeds",
        "agent-contracts lint reports no errors",
        "Cross-references for newly added entities are correct"
      ],
      "execution_steps": [
        {
          "id": "read-current-dsl",
          "action": "Read current DSL definitions and understand the structure",
          "reads_artifact": "dsl-source",
          "required": true
        },
        {
          "id": "update-dsl",
          "action": "Create or update DSL definitions",
          "produces_artifact": "dsl-source"
        },
        {
          "id": "validate",
          "action": "Run agent-contracts validate",
          "uses_tool": "agent-contracts-cli",
          "required": true
        },
        {
          "id": "lint",
          "action": "Run agent-contracts lint",
          "uses_tool": "agent-contracts-cli"
        }
      ],
      "escalation_criteria": [
        {
          "condition": "Schema errors from validate cannot be resolved",
          "action": "stop_and_report"
        }
      ]
    },
    "update-dsl-binding": {
      "description": "Create new or update existing software bindings",
      "target_agent": "dsl-designer",
      "workflow": "dsl-update",
      "input_artifacts": [
        "dsl-source"
      ],
      "invocation_handoff": "dsl-task-request",
      "result_handoff": "dsl-task-result",
      "validations": [
        "dsl-schema-validation",
        "dsl-lint-validation"
      ],
      "responsibilities": [
        "Add and update guardrail_impl check definitions",
        "Configure outputs section templates and inline templates",
        "Set up binding extends inheritance"
      ],
      "completion_criteria": [
        "Binding YAML conforms to the correct schema",
        "guardrail_impl hook_event / matcher values are valid",
        "agent-contracts generate guardrails succeeds"
      ],
      "execution_steps": [
        {
          "id": "read-guardrails",
          "action": "Review target guardrail definitions and policies",
          "reads_artifact": "dsl-source",
          "required": true
        },
        {
          "id": "update-binding",
          "action": "Create or update binding YAML",
          "produces_artifact": "dsl-source"
        },
        {
          "id": "generate",
          "action": "Run agent-contracts generate guardrails to verify",
          "uses_tool": "agent-contracts-cli",
          "required": true
        }
      ],
      "escalation_criteria": [
        {
          "condition": "Binding guardrail_impl is inconsistent with DSL guardrails",
          "action": "stop_and_report"
        }
      ]
    },
    "render-dsl-outputs": {
      "description": "Render prompts and documents from DSL and check for drift",
      "target_agent": "dsl-designer",
      "workflow": "dsl-update",
      "input_artifacts": [
        "dsl-source"
      ],
      "invocation_handoff": "dsl-task-request",
      "result_handoff": "dsl-task-result",
      "validations": [
        "dsl-schema-validation",
        "dsl-completeness-audit"
      ],
      "responsibilities": [
        "Generate prompts and documents via agent-contracts render",
        "Detect and resolve drift using render --check"
      ],
      "completion_criteria": [
        "render succeeds",
        "render --check reports no drift"
      ],
      "execution_steps": [
        {
          "id": "render",
          "action": "Run agent-contracts render",
          "uses_tool": "agent-contracts-cli",
          "produces_artifact": "dsl-generated-output",
          "required": true
        },
        {
          "id": "check-drift",
          "action": "Run agent-contracts render --check to verify no drift",
          "uses_tool": "agent-contracts-cli"
        }
      ],
      "escalation_criteria": [
        {
          "condition": "Unresolved template errors during render",
          "action": "stop_and_report"
        }
      ]
    },
    "check-dsl-score": {
      "description": "Check DSL completeness score and identify improvement areas",
      "target_agent": "dsl-designer",
      "workflow": "dsl-update",
      "input_artifacts": [
        "dsl-source"
      ],
      "invocation_handoff": "dsl-task-request",
      "result_handoff": "dsl-task-result",
      "validations": [
        "dsl-score-validation",
        "dsl-score-report-validation"
      ],
      "responsibilities": [
        "Review score across 7 dimensions and identify weaknesses",
        "List entities that need improvement"
      ],
      "completion_criteria": [
        "Score results have been reviewed",
        "Improvement areas have been identified"
      ],
      "execution_steps": [
        {
          "id": "run-score",
          "action": "Run agent-contracts score",
          "uses_tool": "agent-contracts-cli",
          "required": true
        },
        {
          "id": "analyze",
          "action": "Analyze score weaknesses and formulate improvement plan",
          "produces_artifact": "dsl-score-report"
        }
      ],
      "escalation_criteria": [
        {
          "condition": "Score is significantly below threshold with no clear improvement path",
          "action": "stop_and_report"
        }
      ]
    },
    "audit-dsl-completeness": {
      "description": "Audit completeness of DSL definitions against generated prompts",
      "target_agent": "dsl-auditor",
      "workflow": "dsl-audit",
      "input_artifacts": [
        "dsl-source",
        "dsl-generated-output"
      ],
      "invocation_handoff": "dsl-task-request",
      "result_handoff": "dsl-audit-result",
      "validations": [
        "dsl-completeness-audit",
        "dsl-audit-report-validation"
      ],
      "responsibilities": [
        "Cross-check across 19 dimensions per audit procedure",
        "Classify gaps as template gap, data gap, or DSL gap",
        "Present improvement recommendations"
      ],
      "completion_criteria": [
        "All dimensions inspected for all agents",
        "Detected gaps are classified",
        "Improvement recommendations are presented with priority"
      ],
      "execution_steps": [
        {
          "id": "collect-sources",
          "action": "Collect DSL definitions, generated prompts, and templates",
          "reads_artifact": "dsl-source",
          "required": true
        },
        {
          "id": "collect-outputs",
          "action": "Collect generated prompts",
          "reads_artifact": "dsl-generated-output",
          "required": true
        },
        {
          "id": "run-audit",
          "action": "Execute 19-dimension cross-check"
        },
        {
          "id": "analyze-gaps",
          "action": "Analyze root causes of detected gaps"
        },
        {
          "id": "produce-report",
          "action": "Produce audit report and improvement recommendations",
          "produces_artifact": "dsl-audit-report"
        }
      ],
      "escalation_criteria": [
        {
          "condition": "3 or more critical-level gaps detected",
          "action": "stop_and_report"
        }
      ]
    },
    "audit-semantic-design": {
      "description": "Audit DSL design for semantic coherence",
      "target_agent": "dsl-auditor",
      "workflow": "dsl-audit",
      "input_artifacts": [
        "dsl-source"
      ],
      "invocation_handoff": "dsl-task-request",
      "result_handoff": "dsl-audit-result",
      "validations": [
        "dsl-completeness-audit",
        "dsl-audit-report-validation"
      ],
      "responsibilities": [
        "Check dispatch_only agents for implementation responsibilities",
        "Detect agents with overly broad scope",
        "Identify role overlap between agents",
        "Verify handoff schemas carry sufficient fields for task completion_criteria",
        "Check workflow gates are placed effectively",
        "Detect guardrails declared but absent from execution path",
        "Check semantic validations are distributed across phases",
        "Detect custom x- properties that replicate standard DSL control-flow features (gate, decision, entry_conditions)"
      ],
      "completion_criteria": [
        "All agents reviewed for scope and overlap",
        "Workflow gate placement analyzed",
        "Guardrail enforcement path verified",
        "Custom x- property misuse flagged",
        "Findings classified with severity and category"
      ],
      "execution_steps": [
        {
          "id": "load-dsl",
          "action": "Load resolved DSL definitions",
          "reads_artifact": "dsl-source",
          "required": true
        },
        {
          "id": "analyze-agents",
          "action": "Review agent responsibilities, permissions, and role boundaries"
        },
        {
          "id": "analyze-workflows",
          "action": "Analyze workflow structure, gate placement, and validation coverage"
        },
        {
          "id": "analyze-guardrails",
          "action": "Verify guardrail enforcement paths"
        },
        {
          "id": "produce-report",
          "action": "Produce semantic design audit report",
          "produces_artifact": "dsl-audit-report"
        }
      ],
      "escalation_criteria": [
        {
          "condition": "3 or more critical-level design issues detected",
          "action": "stop_and_report"
        }
      ]
    },
    "audit-generated-prompts": {
      "description": "Audit generated prompts against DSL intent",
      "target_agent": "dsl-auditor",
      "workflow": "dsl-audit",
      "input_artifacts": [
        "dsl-source",
        "dsl-generated-output"
      ],
      "invocation_handoff": "dsl-task-request",
      "result_handoff": "dsl-audit-result",
      "validations": [
        "dsl-completeness-audit",
        "dsl-audit-report-validation"
      ],
      "responsibilities": [
        "Compare generated prompt content against DSL agent definitions",
        "Detect missing requirements from DSL",
        "Detect hallucinated permissions not in DSL",
        "Identify ambiguous or unsafe instructions",
        "Verify consistency between prompt and handoff schema expectations"
      ],
      "completion_criteria": [
        "All generated prompts compared against DSL definitions",
        "Missing, hallucinated, and ambiguous instructions identified",
        "Findings classified with severity and category"
      ],
      "execution_steps": [
        {
          "id": "collect-sources",
          "action": "Collect DSL agent definitions and related entities",
          "reads_artifact": "dsl-source",
          "required": true
        },
        {
          "id": "collect-prompts",
          "action": "Collect generated prompt files",
          "reads_artifact": "dsl-generated-output",
          "required": true
        },
        {
          "id": "compare",
          "action": "Compare each prompt against its DSL definition"
        },
        {
          "id": "produce-report",
          "action": "Produce prompt audit report",
          "produces_artifact": "dsl-audit-report"
        }
      ],
      "escalation_criteria": [
        {
          "condition": "Hallucinated permissions detected in generated prompts",
          "action": "stop_and_report"
        }
      ]
    },
    "audit-extension-consumption": {
      "description": "Audit declared x-* extensions for consumption gaps across render and runtime paths",
      "target_agent": "dsl-auditor",
      "workflow": "dsl-audit",
      "input_artifacts": [
        "dsl-source",
        "dsl-generated-output"
      ],
      "invocation_handoff": "dsl-task-request",
      "result_handoff": "dsl-audit-result",
      "validations": [
        "dsl-completeness-audit",
        "dsl-audit-report-validation"
      ],
      "responsibilities": [
        "Cross-check extensions declarations against entity x-* usage",
        "Identify x-* properties populated in DSL but not consumed by any render template",
        "Detect semantic overlap between x-* extensions and standard DSL features",
        "Report runtime codegen reachability for each extension",
        "Distinguish intentional metadata-only extensions from consumption gaps"
      ],
      "completion_criteria": [
        "All declared extensions checked for template and runtime consumption",
        "Unused extensions flagged with suggested action (remove, migrate to standard, or add template support)",
        "Runtime-unreachable extensions documented with explanation",
        "Findings classified with severity and category"
      ],
      "execution_steps": [
        {
          "id": "collect-declarations",
          "action": "Collect extensions declarations and x-* usage map",
          "reads_artifact": "dsl-source",
          "required": true
        },
        {
          "id": "collect-templates",
          "action": "Collect render template x-* references",
          "reads_artifact": "dsl-generated-output",
          "required": true
        },
        {
          "id": "analyze-consumption",
          "action": "Cross-check declarations vs usage vs template references vs runtime fields"
        },
        {
          "id": "analyze-semantics",
          "action": "Detect semantic overlap with standard DSL features"
        },
        {
          "id": "produce-report",
          "action": "Produce extension consumption audit report",
          "produces_artifact": "dsl-audit-report"
        }
      ],
      "escalation_criteria": [
        {
          "condition": "Required extension with no consumption path detected",
          "action": "stop_and_report"
        }
      ]
    }
  },
  "artifacts": {
    "dsl-source": {
      "type": "config",
      "description": "agent-contracts DSL source files — YAML definitions for agents, tasks, artifacts, tools, validations, handoff_types, workflow, policies, guardrails, guardrail_policies, and bindings.",
      "states": [
        "draft",
        "validated",
        "rendered"
      ],
      "required_validations": [
        "dsl-schema-validation",
        "dsl-lint-validation"
      ]
    },
    "dsl-generated-output": {
      "type": "doc",
      "description": "Files generated from DSL via render — agent prompts, overview documents, workflow commands, guardrail configurations, etc.",
      "states": [
        "generated",
        "verified"
      ],
      "required_validations": [
        "dsl-completeness-audit"
      ]
    },
    "dsl-score-report": {
      "type": "doc",
      "description": "Output of agent-contracts score command — completeness evaluation across 7 dimensions.",
      "states": [
        "generated"
      ],
      "required_validations": [
        "dsl-score-report-validation"
      ]
    },
    "dsl-audit-report": {
      "type": "doc",
      "description": "DSL completeness audit report — PASS/MISS matrix across 19 dimensions, gap analysis, and improvement recommendations.",
      "states": [
        "submitted"
      ],
      "required_validations": [
        "dsl-audit-report-validation"
      ],
      "classification": "evidence"
    }
  },
  "tools": {
    "agent-contracts-cli": {
      "kind": "cli",
      "description": "agent-contracts CLI — execute resolve, validate, lint, render, score, check, and generate commands for the DSL.",
      "input_artifacts": [
        "dsl-source"
      ],
      "output_artifacts": [
        "dsl-generated-output",
        "dsl-score-report"
      ],
      "invokable_by": [
        "dsl-designer",
        "dsl-auditor"
      ],
      "side_effects": [],
      "commands": [
        {
          "command": "npx agent-contracts validate",
          "category": "verification",
          "reads": [
            "dsl-source"
          ],
          "writes": [],
          "purpose": "DSL schema validation and reference checks"
        },
        {
          "command": "npx agent-contracts lint",
          "category": "verification",
          "reads": [
            "dsl-source"
          ],
          "writes": [],
          "purpose": "Semantic lint"
        },
        {
          "command": "npx agent-contracts render -c agent-contracts.config.yaml",
          "category": "generation",
          "reads": [
            "dsl-source"
          ],
          "writes": [
            "dsl-generated-output"
          ],
          "purpose": "Template rendering (prompt and document generation)"
        },
        {
          "command": "npx agent-contracts render -c agent-contracts.config.yaml --check",
          "category": "verification",
          "reads": [
            "dsl-source",
            "dsl-generated-output"
          ],
          "writes": [],
          "purpose": "Drift detection (source vs generated output)"
        },
        {
          "command": "npx agent-contracts score",
          "category": "verification",
          "reads": [
            "dsl-source"
          ],
          "writes": [
            "dsl-score-report"
          ],
          "purpose": "DSL completeness score calculation (7 dimensions)"
        },
        {
          "command": "npx agent-contracts check -c agent-contracts.config.yaml",
          "category": "verification",
          "reads": [
            "dsl-source"
          ],
          "writes": [],
          "purpose": "Pipeline — resolve → validate → lint → render --check"
        },
        {
          "command": "npx agent-contracts generate guardrails",
          "category": "generation",
          "reads": [
            "dsl-source"
          ],
          "writes": [
            "dsl-generated-output"
          ],
          "purpose": "Generate guardrail runtime artifacts"
        }
      ]
    }
  },
  "validations": {
    "dsl-schema-validation": {
      "target_artifact": "dsl-source",
      "kind": "schema",
      "executor_type": "tool",
      "executor": "agent-contracts-cli",
      "blocking": true,
      "description": "DSL schema validation via agent-contracts validate command. Performs type checks on all entities, verifies required fields, and checks cross-reference integrity."
    },
    "dsl-lint-validation": {
      "target_artifact": "dsl-source",
      "kind": "semantic",
      "executor_type": "tool",
      "executor": "agent-contracts-cli",
      "blocking": false,
      "description": "Semantic validation via agent-contracts lint command. Checks naming conventions, unused entities, circular references, and other design quality aspects."
    },
    "dsl-score-validation": {
      "target_artifact": "dsl-source",
      "kind": "semantic",
      "executor_type": "tool",
      "executor": "agent-contracts-cli",
      "blocking": false,
      "description": "Completeness evaluation via agent-contracts score command. Scores across 7 dimensions: artifact validation coverage, task validation coverage, guardrail policy coverage, workflow validation integration, schema completeness, cross-reference bidirectionality, guardrail scope resolution."
    },
    "dsl-completeness-audit": {
      "target_artifact": "dsl-generated-output",
      "kind": "semantic",
      "executor_type": "agent",
      "executor": "dsl-auditor",
      "blocking": false,
      "produces_evidence": "dsl-audit-report",
      "description": "19-dimension cross-check audit of DSL definitions against generated prompts. Verifies that all items defined in the DSL are correctly reflected in generated output."
    },
    "dsl-score-report-validation": {
      "target_artifact": "dsl-score-report",
      "kind": "semantic",
      "executor_type": "tool",
      "executor": "agent-contracts-cli",
      "blocking": false,
      "description": "Validates that dsl-score-report has been produced and contains a valid score across all 7 dimensions."
    },
    "dsl-audit-report-validation": {
      "target_artifact": "dsl-audit-report",
      "kind": "semantic",
      "executor_type": "agent",
      "executor": "dsl-auditor",
      "blocking": false,
      "description": "Validates that dsl-audit-report contains findings classified as PASS/MISS/PARTIAL for all inspected dimensions."
    }
  },
  "handoff_types": {
    "dsl-task-request": {
      "version": 1,
      "description": "Request to execute a DSL management task",
      "schema": {
        "type": "object",
        "properties": {
          "task_id": {
            "type": "string",
            "description": "Identifier of the task to execute"
          },
          "scope": {
            "type": "string",
            "description": "Scope of changes (agents, tasks, artifacts, workflow, bindings, etc.)"
          },
          "context": {
            "type": "string",
            "description": "Background and purpose of the change"
          }
        },
        "required": [
          "task_id"
        ]
      }
    },
    "dsl-task-result": {
      "version": 3,
      "description": "Result of a DSL management task execution",
      "schema": {
        "type": "object",
        "properties": {
          "changed_files": {
            "type": "array",
            "items": {
              "type": "string"
            },
            "description": "List of changed file paths"
          },
          "validation_result": {
            "type": "string",
            "enum": [
              "pass",
              "fail"
            ],
            "description": "Result of agent-contracts validate"
          },
          "lint_result": {
            "type": "string",
            "enum": [
              "pass",
              "fail",
              "skipped"
            ],
            "description": "Result of agent-contracts lint (separate from validate)"
          },
          "generate_result": {
            "type": "string",
            "enum": [
              "pass",
              "fail",
              "skipped"
            ],
            "description": "Result of agent-contracts generate guardrails (binding task). Used by post-binding gate to block render on failure."
          },
          "render_result": {
            "type": "string",
            "enum": [
              "pass",
              "fail",
              "skipped"
            ],
            "description": "Result of agent-contracts render execution. Distinct from drift_detected — captures whether render itself succeeded."
          },
          "drift_detected": {
            "type": "boolean",
            "description": "True if render --check detected drift between DSL source and generated outputs"
          },
          "cross_reference_check": {
            "type": "string",
            "enum": [
              "pass",
              "fail",
              "skipped"
            ],
            "description": "Result of cross-reference integrity verification for newly added entities (update-dsl-definitions completion criterion)."
          },
          "score": {
            "type": "number",
            "description": "Score command result (0-100)"
          },
          "score_dimensions": {
            "type": "object",
            "description": "Per-dimension score breakdown"
          },
          "notes": {
            "type": "string",
            "description": "Additional notes or improvement suggestions"
          }
        },
        "required": [
          "validation_result"
        ]
      }
    },
    "audit-result": {
      "version": 1,
      "description": "Generic audit result for tool/agent execution. Uses canonical agent-audit-result schema from components.schemas.",
      "schema": {
        "type": "object",
        "description": "Top-level result from an agent audit. Canonical schema for agent interoperability across toolchains.",
        "required": [
          "summary",
          "risk_level",
          "findings"
        ],
        "properties": {
          "summary": {
            "type": "string"
          },
          "risk_level": {
            "type": "string",
            "enum": [
              "low",
              "medium",
              "high",
              "critical"
            ]
          },
          "findings": {
            "type": "array",
            "items": {
              "type": "object",
              "description": "A single finding from an agent audit or analysis.",
              "required": [
                "severity",
                "category",
                "message"
              ],
              "properties": {
                "id": {
                  "type": "string",
                  "description": "Unique finding identifier."
                },
                "severity": {
                  "type": "string",
                  "enum": [
                    "info",
                    "warning",
                    "error",
                    "critical"
                  ]
                },
                "category": {
                  "type": "string",
                  "description": "Finding category (e.g. missing-policy, inconsistent-risk)."
                },
                "target": {
                  "type": "string",
                  "description": "Target of the finding (command ID, schema path)."
                },
                "location": {
                  "type": "string",
                  "description": "Location within the target."
                },
                "message": {
                  "type": "string"
                },
                "recommendation": {
                  "type": "string"
                },
                "confidence": {
                  "type": "number",
                  "minimum": 0,
                  "maximum": 1,
                  "description": "Confidence score (0-1) for LLM-generated findings."
                },
                "evidence": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "description": "Evidence supporting an agent finding.",
                    "required": [
                      "kind"
                    ],
                    "properties": {
                      "kind": {
                        "type": "string",
                        "enum": [
                          "file",
                          "command",
                          "schema",
                          "diff",
                          "stdout",
                          "stderr",
                          "text"
                        ]
                      },
                      "target": {
                        "type": "string",
                        "description": "Target identifier (file path, command ID, schema name)."
                      },
                      "location": {
                        "type": "string",
                        "description": "Location within the target (line number, JSON pointer)."
                      },
                      "excerpt": {
                        "type": "string",
                        "description": "Relevant excerpt from the target."
                      }
                    }
                  }
                },
                "details": {
                  "type": "object",
                  "additionalProperties": true
                }
              }
            }
          },
          "recommended_actions": {
            "type": "array",
            "items": {
              "type": "object",
              "description": "A recommended action from an agent audit.",
              "required": [
                "kind",
                "title"
              ],
              "properties": {
                "kind": {
                  "type": "string",
                  "enum": [
                    "run_command",
                    "edit_file",
                    "review",
                    "confirm",
                    "block",
                    "ignore"
                  ]
                },
                "title": {
                  "type": "string"
                },
                "command": {
                  "type": "string",
                  "description": "CLI command to run (for run_command kind)."
                },
                "target": {
                  "type": "string",
                  "description": "Target file or resource."
                },
                "rationale": {
                  "type": "string"
                }
              }
            }
          },
          "metadata": {
            "type": "object",
            "properties": {
              "tool": {
                "type": "string"
              },
              "command": {
                "type": "string"
              },
              "version": {
                "type": "string"
              },
              "generated_at": {
                "type": "string"
              },
              "adapter": {
                "type": "string"
              },
              "model": {
                "type": "string"
              }
            }
          }
        }
      }
    },
    "dsl-audit-result": {
      "version": 4,
      "description": "Result of DSL audit (completeness, semantic design, prompt fidelity, or extensions)",
      "schema": {
        "type": "object",
        "properties": {
          "audit_type": {
            "type": "string",
            "enum": [
              "completeness",
              "semantic",
              "prompt",
              "extensions"
            ],
            "description": "Which audit task produced this result"
          },
          "total_dimensions": {
            "type": "integer",
            "description": "Number of dimensions inspected"
          },
          "pass_count": {
            "type": "integer",
            "description": "Number of PASS dimensions"
          },
          "miss_count": {
            "type": "integer",
            "description": "Number of MISS dimensions"
          },
          "partial_count": {
            "type": "integer",
            "description": "Number of PARTIAL dimensions"
          },
          "agents_reviewed": {
            "type": "integer",
            "description": "Number of agents reviewed (semantic and prompt audits)"
          },
          "prompts_reviewed": {
            "type": "integer",
            "description": "Number of generated prompts compared (prompt audit)"
          },
          "completion_criteria_coverage": {
            "type": "object",
            "properties": {
              "all_dimensions_inspected": {
                "type": "boolean",
                "description": "All required dimensions were inspected for all agents"
              },
              "gaps_classified": {
                "type": "boolean",
                "description": "Every detected gap has a root-cause classification"
              },
              "gate_analysis_complete": {
                "type": "boolean",
                "description": "Workflow gate placement analysis has been performed"
              },
              "guardrail_enforcement_verified": {
                "type": "boolean",
                "description": "All declared guardrails verified to exist in execution paths"
              },
              "scope_overlap_analyzed": {
                "type": "boolean",
                "description": "All agents reviewed for responsibility scope and role overlap"
              },
              "x_property_misuse_checked": {
                "type": "boolean",
                "description": "Custom x-* properties checked for standard DSL overlap"
              },
              "hallucinated_permissions_checked": {
                "type": "boolean",
                "description": "Generated prompts verified for permissions not declared in DSL"
              },
              "extension_consumption_checked": {
                "type": "boolean",
                "description": "All declared extensions checked for template/runtime consumption"
              }
            },
            "description": "Per-criterion completion status. Each field maps to a completion_criteria entry in the corresponding audit task."
          },
          "critical_gaps": {
            "type": "array",
            "items": {
              "type": "object",
              "properties": {
                "dimension": {
                  "type": "string"
                },
                "agent": {
                  "type": "string"
                },
                "gap_type": {
                  "type": "string",
                  "enum": [
                    "template_gap",
                    "data_gap",
                    "dsl_gap"
                  ]
                },
                "severity": {
                  "type": "string",
                  "enum": [
                    "critical",
                    "warning",
                    "info"
                  ]
                }
              }
            },
            "description": "List of detected gaps"
          },
          "recommendations": {
            "type": "array",
            "items": {
              "type": "object",
              "properties": {
                "priority": {
                  "type": "string",
                  "enum": [
                    "P0",
                    "P1",
                    "P2"
                  ]
                },
                "description": {
                  "type": "string"
                },
                "fix_type": {
                  "type": "string",
                  "enum": [
                    "template_fix",
                    "dsl_fix",
                    "regeneration"
                  ]
                }
              }
            },
            "description": "Prioritized improvement recommendation list"
          }
        },
        "required": [
          "audit_type",
          "total_dimensions",
          "pass_count",
          "miss_count",
          "completion_criteria_coverage"
        ]
      }
    }
  },
  "workflow": {
    "dsl-update": {
      "description": "DSL Update — create and update agent-contracts DSL definitions and bindings, verify quality via validate / lint / render / score. Executed by DSL Designer.",
      "entry_conditions": [
        "A DSL definition change request exists"
      ],
      "trigger": "Execute when new DSL definitions need to be created, existing definitions need updating, or bindings need to be added or modified.",
      "steps": [
        {
          "type": "delegate",
          "task": "update-dsl-definitions",
          "from_agent": "dsl-designer",
          "description": "DSL Designer creates or updates DSL definitions and verifies via validate / lint."
        },
        {
          "type": "gate",
          "gate_kind": "dsl-task-result",
          "description": "Block if validation_result or lint_result did not pass in update-dsl-definitions.  Enforces dsl-validate-before-render guardrail — prevents render from executing against invalid DSL."
        },
        {
          "type": "delegate",
          "task": "update-dsl-binding",
          "from_agent": "dsl-designer",
          "description": "DSL Designer adds or updates bindings and verifies via generate."
        },
        {
          "type": "gate",
          "gate_kind": "dsl-task-result",
          "description": "Block if generate_result is not 'pass' in update-dsl-binding. Prevents render-dsl-outputs from executing against invalid bindings."
        },
        {
          "type": "delegate",
          "task": "render-dsl-outputs",
          "from_agent": "dsl-designer",
          "description": "DSL Designer renders prompts and documents and checks for drift."
        },
        {
          "type": "gate",
          "gate_kind": "dsl-task-result",
          "description": "Block if render_result is not 'pass' or drift_detected is true in render-dsl-outputs result."
        },
        {
          "type": "delegate",
          "task": "check-dsl-score",
          "from_agent": "dsl-designer",
          "description": "DSL Designer checks completeness score and identifies improvement areas."
        }
      ]
    },
    "dsl-audit": {
      "description": "DSL Audit — audit completeness of DSL definitions against generated prompts, detect gaps, and present improvement recommendations. Executed by DSL Auditor. Supports four audit types: render (semantic diff), dsl (design coherence), prompt (prompt fidelity), and extensions (x-* consumption gap detection).",
      "entry_conditions": [
        "DSL definition rendering is complete"
      ],
      "trigger": "Execute when DSL completeness audit is needed. Typically run as a quality check after DSL updates.",
      "steps": [
        {
          "type": "delegate",
          "task": "audit-dsl-completeness",
          "from_agent": "dsl-auditor",
          "description": "DSL Auditor executes 19-dimension cross-check and produces audit report with improvement recommendations."
        },
        {
          "type": "gate",
          "gate_kind": "dsl-audit-result",
          "description": "Block if audit-dsl-completeness detected 3 or more critical-level gaps (triggers stop_and_report escalation)."
        },
        {
          "type": "delegate",
          "task": "audit-semantic-design",
          "from_agent": "dsl-auditor",
          "depends_on": [
            "gate:dsl-audit-result"
          ],
          "description": "DSL Auditor reviews DSL design for semantic coherence — role overlap, scope breadth, gate placement, guardrail enforcement paths."
        },
        {
          "type": "gate",
          "gate_kind": "dsl-audit-result",
          "depends_on": [
            "audit-semantic-design"
          ],
          "description": "Semantic-design gate — block if critical design issues detected (gate placement defects, handoff schema gaps)."
        },
        {
          "type": "delegate",
          "task": "audit-generated-prompts",
          "from_agent": "dsl-auditor",
          "depends_on": [
            "gate:dsl-audit-result"
          ],
          "description": "DSL Auditor compares generated prompts against DSL intent — detects missing requirements, hallucinated permissions, ambiguous instructions."
        },
        {
          "type": "gate",
          "gate_kind": "dsl-audit-result",
          "depends_on": [
            "audit-generated-prompts"
          ],
          "description": "Hallucinated-permissions gate — block immediately if audit-generated-prompts detected permissions not declared in DSL (enforces dsl-no-hallucinated-permissions guardrail)."
        },
        {
          "type": "delegate",
          "task": "audit-extension-consumption",
          "from_agent": "dsl-auditor",
          "depends_on": [
            "gate:dsl-audit-result"
          ],
          "description": "DSL Auditor checks x-* extension properties for consumption gaps — declared but unused, populated but not rendered, semantic overlap with standard DSL features."
        },
        {
          "type": "gate",
          "gate_kind": "dsl-audit-result",
          "depends_on": [
            "audit-semantic-design",
            "audit-generated-prompts",
            "audit-extension-consumption"
          ],
          "description": "Terminal gate — aggregates all audit results and blocks if any critical-level findings remain unresolved."
        }
      ]
    }
  },
  "guardrails": {
    "dsl-readonly-enforcement": {
      "description": "Prevent audit-purpose agents from modifying DSL source files. dsl-auditor is mode=read-write (to produce dsl-audit-report) but must not write to dsl-source. This guardrail enforces that boundary.",
      "scope": {
        "agents": [
          "dsl-auditor"
        ],
        "artifacts": [
          "dsl-source"
        ]
      },
      "rationale": "dsl-auditor uses mode=read-write because it produces dsl-audit-report, but its constraints prohibit modifying DSL definitions. This guardrail blocks writes to dsl-source from audit-purpose agents."
    },
    "dsl-validate-before-render": {
      "description": "Ensure DSL validation passes before rendering is attempted. Prevents generation of prompts from invalid DSL definitions.",
      "scope": {
        "workflows": [
          "dsl-update"
        ],
        "tasks": [
          "render-dsl-outputs"
        ],
        "artifacts": [
          "dsl-generated-output"
        ]
      },
      "rationale": "render-dsl-outputs must not be invoked while validation_result of update-dsl-definitions is not 'pass'."
    },
    "dsl-no-hallucinated-permissions": {
      "description": "Rendered prompts must not grant permissions not declared in DSL. Detected via audit-generated-prompts; any hallucinated-permission finding of severity critical must be escalated.",
      "scope": {
        "artifacts": [
          "dsl-generated-output"
        ],
        "tasks": [
          "audit-generated-prompts"
        ],
        "tools": [
          "agent-contracts-cli"
        ]
      },
      "rationale": "audit-generated-prompts detects permissions or tool references in generated prompts that have no corresponding declaration in the agent's DSL definition."
    }
  },
  "guardrail_policies": {
    "dsl-management-policy": {
      "description": "Guardrail policy governing DSL management operations. Groups all DSL-related guardrails and specifies resolution behavior.",
      "rules": [
        {
          "guardrail": "dsl-readonly-enforcement",
          "severity": "critical",
          "action": "block"
        },
        {
          "guardrail": "dsl-validate-before-render",
          "severity": "mandatory",
          "action": "block"
        },
        {
          "guardrail": "dsl-no-hallucinated-permissions",
          "severity": "critical",
          "action": "block",
          "escalation": {
            "target": "dsl-designer",
            "condition": "Hallucinated permissions detected in generated prompts"
          }
        }
      ]
    }
  },
  "components": {
    "schemas": {
      "agent-evidence": {
        "type": "object",
        "description": "Evidence supporting an agent finding.",
        "required": [
          "kind"
        ],
        "properties": {
          "kind": {
            "type": "string",
            "enum": [
              "file",
              "command",
              "schema",
              "diff",
              "stdout",
              "stderr",
              "text"
            ]
          },
          "target": {
            "type": "string",
            "description": "Target identifier (file path, command ID, schema name)."
          },
          "location": {
            "type": "string",
            "description": "Location within the target (line number, JSON pointer)."
          },
          "excerpt": {
            "type": "string",
            "description": "Relevant excerpt from the target."
          }
        }
      },
      "agent-finding": {
        "type": "object",
        "description": "A single finding from an agent audit or analysis.",
        "required": [
          "severity",
          "category",
          "message"
        ],
        "properties": {
          "id": {
            "type": "string",
            "description": "Unique finding identifier."
          },
          "severity": {
            "type": "string",
            "enum": [
              "info",
              "warning",
              "error",
              "critical"
            ]
          },
          "category": {
            "type": "string",
            "description": "Finding category (e.g. missing-policy, inconsistent-risk)."
          },
          "target": {
            "type": "string",
            "description": "Target of the finding (command ID, schema path)."
          },
          "location": {
            "type": "string",
            "description": "Location within the target."
          },
          "message": {
            "type": "string"
          },
          "recommendation": {
            "type": "string"
          },
          "confidence": {
            "type": "number",
            "minimum": 0,
            "maximum": 1,
            "description": "Confidence score (0-1) for LLM-generated findings."
          },
          "evidence": {
            "type": "array",
            "items": {
              "type": "object",
              "description": "Evidence supporting an agent finding.",
              "required": [
                "kind"
              ],
              "properties": {
                "kind": {
                  "type": "string",
                  "enum": [
                    "file",
                    "command",
                    "schema",
                    "diff",
                    "stdout",
                    "stderr",
                    "text"
                  ]
                },
                "target": {
                  "type": "string",
                  "description": "Target identifier (file path, command ID, schema name)."
                },
                "location": {
                  "type": "string",
                  "description": "Location within the target (line number, JSON pointer)."
                },
                "excerpt": {
                  "type": "string",
                  "description": "Relevant excerpt from the target."
                }
              }
            }
          },
          "details": {
            "type": "object",
            "additionalProperties": true
          }
        }
      },
      "agent-recommended-action": {
        "type": "object",
        "description": "A recommended action from an agent audit.",
        "required": [
          "kind",
          "title"
        ],
        "properties": {
          "kind": {
            "type": "string",
            "enum": [
              "run_command",
              "edit_file",
              "review",
              "confirm",
              "block",
              "ignore"
            ]
          },
          "title": {
            "type": "string"
          },
          "command": {
            "type": "string",
            "description": "CLI command to run (for run_command kind)."
          },
          "target": {
            "type": "string",
            "description": "Target file or resource."
          },
          "rationale": {
            "type": "string"
          }
        }
      },
      "agent-audit-result": {
        "type": "object",
        "description": "Top-level result from an agent audit. Canonical schema for agent interoperability across toolchains.",
        "required": [
          "summary",
          "risk_level",
          "findings"
        ],
        "properties": {
          "summary": {
            "type": "string"
          },
          "risk_level": {
            "type": "string",
            "enum": [
              "low",
              "medium",
              "high",
              "critical"
            ]
          },
          "findings": {
            "type": "array",
            "items": {
              "type": "object",
              "description": "A single finding from an agent audit or analysis.",
              "required": [
                "severity",
                "category",
                "message"
              ],
              "properties": {
                "id": {
                  "type": "string",
                  "description": "Unique finding identifier."
                },
                "severity": {
                  "type": "string",
                  "enum": [
                    "info",
                    "warning",
                    "error",
                    "critical"
                  ]
                },
                "category": {
                  "type": "string",
                  "description": "Finding category (e.g. missing-policy, inconsistent-risk)."
                },
                "target": {
                  "type": "string",
                  "description": "Target of the finding (command ID, schema path)."
                },
                "location": {
                  "type": "string",
                  "description": "Location within the target."
                },
                "message": {
                  "type": "string"
                },
                "recommendation": {
                  "type": "string"
                },
                "confidence": {
                  "type": "number",
                  "minimum": 0,
                  "maximum": 1,
                  "description": "Confidence score (0-1) for LLM-generated findings."
                },
                "evidence": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "description": "Evidence supporting an agent finding.",
                    "required": [
                      "kind"
                    ],
                    "properties": {
                      "kind": {
                        "type": "string",
                        "enum": [
                          "file",
                          "command",
                          "schema",
                          "diff",
                          "stdout",
                          "stderr",
                          "text"
                        ]
                      },
                      "target": {
                        "type": "string",
                        "description": "Target identifier (file path, command ID, schema name)."
                      },
                      "location": {
                        "type": "string",
                        "description": "Location within the target (line number, JSON pointer)."
                      },
                      "excerpt": {
                        "type": "string",
                        "description": "Relevant excerpt from the target."
                      }
                    }
                  }
                },
                "details": {
                  "type": "object",
                  "additionalProperties": true
                }
              }
            }
          },
          "recommended_actions": {
            "type": "array",
            "items": {
              "type": "object",
              "description": "A recommended action from an agent audit.",
              "required": [
                "kind",
                "title"
              ],
              "properties": {
                "kind": {
                  "type": "string",
                  "enum": [
                    "run_command",
                    "edit_file",
                    "review",
                    "confirm",
                    "block",
                    "ignore"
                  ]
                },
                "title": {
                  "type": "string"
                },
                "command": {
                  "type": "string",
                  "description": "CLI command to run (for run_command kind)."
                },
                "target": {
                  "type": "string",
                  "description": "Target file or resource."
                },
                "rationale": {
                  "type": "string"
                }
              }
            }
          },
          "metadata": {
            "type": "object",
            "properties": {
              "tool": {
                "type": "string"
              },
              "command": {
                "type": "string"
              },
              "version": {
                "type": "string"
              },
              "generated_at": {
                "type": "string"
              },
              "adapter": {
                "type": "string"
              },
              "model": {
                "type": "string"
              }
            }
          }
        }
      }
    }
  }
} as const;
